import { inject, injectable } from 'inversify'
import { isNil, omitBy, pick } from 'lodash-es'
import { Group, Matrix4, type Object3D, Plane, Quaternion, Vector3 } from 'three'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import type { Layer } from '@/plugins/cad/module/CADModule/model/layer/Layer'
import { CadClipBinding } from '@/plugins/cad/module/CADModule/object/material/CadClipBinding'
import {
  getRegisteredCadWebGpuLineModule,
  setCadClipBinding,
} from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import type { IUpdateOptions } from '@/plugins/cad/module/CADModule/object/objectManager/BaseObjectManager'
import {
  type CadRenderInvalidator,
  NOOP_CAD_RENDER_INVALIDATOR,
} from '@/plugins/cad/module/CADModule/renderInvalidation'
import { updateGroupByPlane } from '@/plugins/cad/module/common/utils'
import { TYPES } from '@/plugins/cad/module/container/types'
import { createCompiledClipState } from '@/shared/clipping/CompiledClipState'
import type { ClipVolume } from '@/shared/types/core'
import { DataManager, DefaultLayerId, type IPlane } from './DataManager'
import { ObjectManager } from './ObjectManager'

/**
 * CAD 视图管理器，协调数据与对象管理器并维护绘图平面与临时辅助对象。
 */
@injectable()
export class CADViewManager {
  @inject(TYPES.RenderInvalidator)
  private readonly renderInvalidator: CadRenderInvalidator = NOOP_CAD_RENDER_INVALIDATOR

  private _dataManager: DataManager = new DataManager()
  private _objectManager: ObjectManager = new ObjectManager()
  private _planeHelper: Plane = new Plane(new Vector3(0, 0, 1), 0) // 默认XY平面
  private readonly _workPlane: IPlane = {
    origin: new Vector3(0, 0, 0),
    normal: new Vector3(0, 0, 1),
    up: new Vector3(0, 1, 0),
    elevation: 0,
  }
  /** 临时辅助对象容器（极轴线、捕捉标记等），与实体 Group 同级 */
  private readonly _tempGroup: Group = new Group()
  private readonly patchedTempObjects = new WeakSet<Object3D>()
  private _clipBinding: CadClipBinding | null = null

  /** 数据管理器 */
  get dataManager() {
    return this._dataManager
  }

  /** 对象管理器 */
  get objectManager() {
    return this._objectManager
  }

  /** 当前绘图平面辅助对象 */
  get planeHelper() {
    return this._planeHelper
  }

  /** 已叠加实体组世界变换的当前工作平面 */
  get workPlane() {
    return this._workPlane
  }

  /** 临时辅助对象 Group（极轴线、捕捉标记等） */
  get tempGroup() {
    return this._tempGroup
  }

  /**
   * 绑定数据管理器与对象管理器，并根据数据同步绘图平面。
   *
   * @param dataManager - 数据管理器
   * @param objectManager - 对象管理器
   */
  set(dataManager: DataManager, objectManager: ObjectManager) {
    this._dataManager = dataManager
    this._objectManager = objectManager
    this.syncWorkingTransformFromObjectGroup()
  }

  /** 将 ClipService 的全局裁切体应用到 CAD WebGPU 材质。 */
  setClipVolumes(volumes: ReadonlyArray<ClipVolume>): void {
    const tsl = getRegisteredCadWebGpuLineModule()?.tsl
    if (!tsl) return

    const state = createCompiledClipState(volumes)
    if (state.volumes.length === 0) {
      this.clearClipBinding()
      return
    }

    const binding = new CadClipBinding(tsl, state)
    const previous = this._clipBinding
    this._clipBinding = binding
    setCadClipBinding(binding)
    this.objectManager.setClipBinding(binding)
    previous?.dispose()
  }

  /** 清除 CAD 材质上的裁切装饰与 GPU packet。 */
  clearClipBinding(): void {
    this._clipBinding?.dispose()
    this._clipBinding = null
    setCadClipBinding(null)
    this.objectManager.setClipBinding(null)
  }

  /** 释放视图管理器持有的裁切资源。 */
  dispose(): void {
    this.clearClipBinding()
  }

  /**
   * 从实体组当前世界矩阵同步工作平面与临时辅助组。
   *
   * 外部修改实体组位姿后调用此方法，可确保拾取、网格和临时图元使用同一坐标系。
   */
  syncWorkingTransformFromObjectGroup() {
    const objectGroup = this.getObjectGroup()
    objectGroup.updateWorldMatrix(true, false)

    const worldQuaternion = new Quaternion()
    const worldScale = new Vector3()
    objectGroup.matrixWorld.decompose(this._workPlane.origin, worldQuaternion, worldScale)
    this._workPlane.normal.set(0, 0, 1).applyQuaternion(worldQuaternion).normalize()
    this._workPlane.up.set(0, 1, 0).applyQuaternion(worldQuaternion).normalize()
    this._workPlane.elevation = this._workPlane.origin.z
    this.planeHelper.setFromNormalAndCoplanarPoint(this._workPlane.normal, this._workPlane.origin)

    const localMatrix = objectGroup.matrixWorld.clone()
    if (this._tempGroup.parent) {
      this._tempGroup.parent.updateWorldMatrix(true, false)
      const inverseParentMatrix = new Matrix4().copy(this._tempGroup.parent.matrixWorld).invert()
      localMatrix.premultiply(inverseParentMatrix)
    }
    localMatrix.decompose(
      this._tempGroup.position,
      this._tempGroup.quaternion,
      this._tempGroup.scale
    )
    this._tempGroup.updateMatrix()
    this._tempGroup.updateMatrixWorld(true)
    this.renderInvalidator.invalidate('cad.tempGroup.transform')
  }

  /**
   * 获取承载所有实体渲染对象的场景 Group。
   *
   * @returns 实体渲染对象容器
   */
  getObjectGroup() {
    return this.objectManager.getSceneGroup()
  }

  /**
   * 将临时辅助对象 Group 挂载到给定父节点下。
   *
   * @param parent - 父 Object3D
   */
  mountTempGroup(parent: Object3D) {
    if (this._tempGroup.parent !== parent) {
      parent.add(this._tempGroup)
      this.syncWorkingTransformFromObjectGroup()
      this.renderInvalidator.invalidate('cad.tempGroup.mounted')
    }
  }

  /** 从父节点卸载临时辅助对象 Group。 */
  unmountTempGroup() {
    if (this._tempGroup.parent) {
      this._tempGroup.removeFromParent()
      this.renderInvalidator.invalidate('cad.tempGroup.unmounted')
    }
  }

  /** 清空临时辅助对象 Group 下的所有子对象。 */
  clearTempObjects() {
    if (this._tempGroup.children.length === 0) {
      return
    }
    this._tempGroup.clear()
    this.renderInvalidator.invalidate('cad.tempGroup.cleared')
  }

  /**
   * 将临时辅助对象加入辅助 Group。
   *
   * @param object - 要附加的 Object3D
   */
  attachTempObject(object: Object3D) {
    this.patchTempObjectMutation(object)
    this._tempGroup.add(object)
    this.renderInvalidator.invalidate('cad.tempObject.attached')
  }

  /**
   * 根据绘图平面更新临时辅助 Group 的朝向与位置。
   *
   * @param plane - 绘图平面
   */
  updateTempGroupByPlane(plane: IPlane) {
    updateGroupByPlane(this._tempGroup, plane)
    this.renderInvalidator.invalidate('cad.tempGroup.transform')
  }

  private patchTempObjectMutation(object: Object3D) {
    if (this.patchedTempObjects.has(object)) {
      return
    }
    this.patchedTempObjects.add(object)

    const invalidate = (reason: string) => this.renderInvalidator.invalidate(reason)
    const originalAdd = object.add.bind(object)
    const originalRemove = object.remove.bind(object)
    const originalClear = object.clear.bind(object)

    object.add = (...children: Object3D[]) => {
      const before = object.children.length
      const result = originalAdd(...children)
      if (object.children.length !== before) {
        invalidate('cad.tempObject.childrenChanged')
      }
      return result
    }

    object.remove = (...children: Object3D[]) => {
      const before = object.children.length
      const result = originalRemove(...children)
      if (object.children.length !== before) {
        invalidate('cad.tempObject.childrenChanged')
      }
      return result
    }

    object.clear = () => {
      if (object.children.length === 0) {
        return originalClear()
      }
      const result = originalClear()
      invalidate('cad.tempObject.childrenChanged')
      return result
    }
  }

  /**
   * 新增图层。
   *
   * @param layer - 待添加图层
   */
  addLayer(layer: Layer) {
    this.dataManager.addLayer(layer)
  }

  /**
   * 批量新增图层。
   *
   * @param layers - 待添加图层列表
   */
  addLayers(layers: Layer[]) {
    for (const layer of layers) {
      this.dataManager.addLayer(layer)
    }
  }

  /**
   * 删除指定 id 的图层。
   *
   * @param id - 图层 id
   */
  deleteLayer(id: string) {
    this.dataManager.deleteLayer(id)
  }

  /**
   * 更新图层。
   *
   * @param layer - 新图层数据，按 id 覆盖原图层
   */
  updateLayer(layer: Layer, previousVisible = this.dataManager.getLayer(layer.id)?.visible) {
    this.dataManager.updateLayer(layer)
    if (previousVisible !== undefined && previousVisible !== layer.visible) {
      this.objectManager.setEntitiesVisible(this.getEntitiesByLayerId(layer.id), layer.visible)
    }
  }

  /**
   * 新增实体并创建对应渲染对象。
   *
   * @param entity - 待添加实体
   */
  addEntity(entity: IEntity) {
    this.dataManager.addEntity(entity)
    this.objectManager.addEntity(entity)
  }

  /**
   * 批量新增实体并创建对应渲染对象。
   *
   * @param entities - 待添加实体列表
   */
  addEntities(entities: IEntity[]) {
    this.dataManager.addEntities(entities)
    this.objectManager.addEntities(entities)
  }

  /**
   * 删除实体并移除其渲染对象。
   *
   * @param entity - 待删除实体
   */
  deleteEntity(entity: IEntity) {
    this.dataManager.deleteEntity(entity)
    this.objectManager.deleteEntity(entity)
  }

  /**
   * 批量删除实体并移除对应渲染对象。
   *
   * @param entities - 待删除实体列表
   */
  deleteEntities(entities: IEntity[]) {
    this.dataManager.deleteEntities(entities)
    this.objectManager.deleteEntities(entities)
  }

  /** 删除所有实体及其渲染对象。 */
  deleteAllEntities() {
    this.dataManager.deleteAllEntities()
    this.objectManager.deleteAllEntities()
  }

  /**
   * 更新单个实体并同步其渲染对象。
   *
   * @param entity - 新实体数据
   * @param options - 更新选项，可控制是否重建几何/材质
   */
  updateEntity(entity: IEntity, options?: IUpdateOptions) {
    this.dataManager.updateEntity(entity)
    this.objectManager.updateEntity(entity, options)
  }

  /**
   * 批量更新实体并同步渲染对象。
   *
   * @param entities - 新实体数据列表
   * @param options - 更新选项，可控制是否重建几何/材质
   */
  updateEntities(entities: IEntity[], options?: IUpdateOptions) {
    this.dataManager.updateEntities(entities)
    this.objectManager.updateEntities(entities, options)
  }

  /**
   * 根据 id 获取实体。
   *
   * @param id - 实体 id
   * @returns 实体，未找到返回 undefined
   */
  getEntity(id: string): IEntity | undefined {
    return this.dataManager.getEntity(id)
  }

  /**
   * 将实体置为 hover 高亮状态。
   *
   * @param entity - 目标实体或实体数组
   */
  hoverEntity(entity: IEntity | IEntity[]) {
    this.objectManager.hoverEntity(entity)
  }

  /**
   * 取消实体的 hover 高亮。
   *
   * @param entity - 目标实体或实体数组
   */
  cancelHoverEntity(entity: IEntity | IEntity[]) {
    this.objectManager.cancelHoverEntity(entity)
  }

  /**
   * 将实体置为选中高亮状态。
   *
   * @param entity - 目标实体或实体数组
   */
  selectEntity(entity: IEntity | IEntity[]) {
    this.objectManager.selectEntity(entity)
  }

  /**
   * 取消实体的选中高亮。
   *
   * @param entity - 目标实体或实体数组
   */
  cancelSelectEntity(entity: IEntity | IEntity[]) {
    this.objectManager.cancelSelectEntity(entity)
  }

  /**
   * 获取实体对应的渲染对象。
   *
   * @param entity - 目标实体
   * @returns 渲染对象；未创建时返回 undefined
   */
  getEntityObject(entity: IEntity): Object3D | undefined {
    return this.objectManager.getEntityObject(entity)
  }

  /**
   * 获取所有可见实体。
   *
   * @returns 当前数据管理器中的全部实体
   */
  getVisibleEntities(): IEntity[] {
    return this.dataManager.entities
  }

  /**
   * 按图层 id 筛选实体。
   *
   * @param layerId - 图层 id
   * @returns 属于该图层的实体列表
   */
  getEntitiesByLayerId(layerId: string): IEntity[] {
    return this.dataManager.entities.filter((e) => e.layerId === layerId)
  }

  /**
   * 基于部分初始化数据生成实体的默认公共字段。
   *
   * @param initData - 创建实体时的初始字段
   * @returns 填充后的公共字段子集
   */
  getEntityDefaultData(initData?: Partial<IEntity>): Partial<IEntity> | undefined {
    return {
      ...omitBy(pick(initData ?? {}, ['colorMethod', 'color', 'lineType', 'lineWidth']), isNil),
      layerId: initData?.layerId || DefaultLayerId,
    }
  }
}
